import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { Plus, X } from "lucide-react"
import * as React from "react"
import {
	type ContestCreateFormValues,
	ContestDatesForm,
} from "@/components/contests/contest-dates-form"
import { PageHeader, Section, SectionStack } from "@/components/layout"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { ContestsAdminGrid } from "@/features/contests-admin/ContestsAdminGrid"
import { SEPT_2026_REFERRAL_CONTEST_ID } from "@/features/contests-admin/types"
import {
	useContestDefinitions,
	useContests,
	useUpsertContestDates,
} from "@/queries/marketplace"

export const Route = createFileRoute("/contests/")({
	component: ContestsPage,
})

const MONTHS = [
	"jan",
	"feb",
	"mar",
	"apr",
	"may",
	"jun",
	"jul",
	"aug",
	"sep",
	"oct",
	"nov",
	"dec",
] as const

/** `sept-2026-referral` for the very first contest, then `<mon>-<year>-referral` for the current month. */
function suggestedContestId(takenIds: string[]): string {
	if (!takenIds.includes(SEPT_2026_REFERRAL_CONTEST_ID)) {
		return SEPT_2026_REFERRAL_CONTEST_ID
	}
	const now = new Date()
	const base = `${MONTHS[now.getUTCMonth()]}-${now.getUTCFullYear()}-referral`
	let candidate = base
	for (let n = 2; takenIds.includes(candidate); n++) {
		candidate = `${base}-${n}`
	}
	return candidate
}

function ContestsPage() {
	const navigate = useNavigate()
	const { contests, isLoading, error } = useContests()
	const { definitions } = useContestDefinitions()
	const upsertMutation = useUpsertContestDates()
	const [showCreate, setShowCreate] = React.useState(false)

	const hasOpenContest = contests.some(
		(contest) => contest.endedProcessedAt === null,
	)
	const isCreateForced = !isLoading && contests.length === 0
	const isCreateVisible =
		!hasOpenContest && definitions.length > 0 && (showCreate || isCreateForced)

	const handleCreate = React.useCallback(
		(values: ContestCreateFormValues) => {
			upsertMutation.mutate(values, {
				onSuccess: ({ contest }) => {
					navigate({
						to: "/contests/$contestId",
						params: { contestId: contest.id },
					})
				},
			})
		},
		[navigate, upsertMutation],
	)

	const headerAction =
		!hasOpenContest && !isLoading && !isCreateForced ? (
			<Button
				variant={isCreateVisible ? "outline" : "default"}
				onClick={() => setShowCreate((current) => !current)}
			>
				{isCreateVisible ? <X /> : <Plus />}
				{isCreateVisible ? "Hide form" : "Create contest"}
			</Button>
		) : null

	return (
		<div className="flex flex-col gap-6 p-6">
			<PageHeader
				title="Contests"
				count={!isLoading && !error ? contests.length : undefined}
				description="Referral contests: dates, standings, closing and awards"
				actions={headerAction}
			/>

			<SectionStack>
				{isCreateVisible ? (
					<Section label="Create a contest">
						<ContestDatesForm
							contestId="new"
							create={{
								definitions,
								defaultId: suggestedContestId(contests.map((c) => c.id)),
							}}
							onSubmit={handleCreate}
							isSubmitting={upsertMutation.isPending}
							submitLabel="Create contest"
						/>
					</Section>
				) : null}

				{isLoading ? (
					<div className="flex flex-col gap-2">
						<Skeleton className="h-9" />
						<Skeleton className="h-9" />
						<Skeleton className="h-9" />
					</div>
				) : error ? (
					<p className="text-bad">{error.message}</p>
				) : contests.length === 0 ? (
					<p className="text-muted-foreground">
						No contest yet. Set the dates above to create the first one.
					</p>
				) : (
					<ContestsAdminGrid contests={contests} />
				)}
			</SectionStack>
		</div>
	)
}
